import React, { useEffect, useRef, useState } from "react";
import { ApiPaths } from "../../Config/ApiPath";
import { setTeamSection } from "../../Redux/TeamSlice";
import "./DirectTeamApp.css";
import useAxiosHelper from "./../../Common/AxiosHelper";
import PaginationComponent from "../../Components/PaginationControls/PaginationControls";
import moment from "moment/moment";
import { BasicInfo } from "../../Config/BasicInfo";
import {
  Calendar,
  Clock,
  Users,
  UserCheck,
  ArrowLeft,
  Search,
  SlidersHorizontal,
  X,
  ChevronRight,
  Layers,
  Lock,
  Unlock,
  Briefcase,
  GitBranch,
  Inbox,
  RotateCcw,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

const EMPTY_SEARCH = {
  name: "",
  username: "",
  startDateJoining: "",
  endDateJoining: "",
  status: "",
};

// Level accent colours (cycled)
const levelTones = ["orange", "blue", "violet", "mint", "pink", "gold"];

const initials = (name, username) =>
  (name || username || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

const DirectTeam = () => {
  const dispatch = useDispatch();
  const [filterVisiblity, setFilterVisiblity] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const { AxiosGet } = useAxiosHelper();
  const [selfBusiness, setSelfBusiness] = useState("");
  const [teamBusiness, setTeamBusiness] = useState("");
  const teamData = useSelector((state) => state.teamData?.teamSection);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limitFilter, setLimitFilter] = useState(10);
  const [levelTeam, setLevelTeam] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showDetails, setShowDetails] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showLevelDetails, setShowLevelDetails] = useState(false);
  const [selectedLevelData, setSelectedLevelData] = useState(null);
  const [openLevelData, setOpenLevelData] = useState([]);
  const [activeLevel, setActiveLevel] = useState(null);
  const [search, setSearch] = useState(EMPTY_SEARCH);
  const [activeTab, setActiveTab] = useState("team");
  // bumped to re-run FetchData after filter state has committed
  const [filterTick, setFilterTick] = useState(0);
  const firstTick = useRef(true);

  const cur = companyData?.token_sign ?? "$";
  const fmt = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return v ?? 0;
    return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Initial data fetch
  useEffect(() => {
    CompanyInfo();
    fetchTeamData();
  }, []);

  // Fetch data when currentPage changes
  useEffect(() => {
    if (activeTab === "team" || (activeTab === "level" && showLevelDetails)) {
      FetchData();
    }
  }, [currentPage, activeTab, showLevelDetails, activeLevel]);

  // Reset pagination when switching tabs
  useEffect(() => {
    if (activeTab === "team") {
      setCurrentPage(1);
      FetchData();
    }
  }, [activeTab]);

  // Fetch data when limit filter changes
  useEffect(() => {
    if (
      (activeTab === "team" || (activeTab === "level" && showLevelDetails)) &&
      limitFilter
    ) {
      FetchData();
    }
  }, [limitFilter]);

  // Filters applied from the sheet / status chips
  useEffect(() => {
    if (firstTick.current) {
      firstTick.current = false;
      return;
    }
    FetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTick]);

  async function CompanyInfo() {
    try {
      const data = localStorage.getItem("companyData");
      setCompanyData(JSON.parse(data));
    } catch (error) {
      BasicInfo.isDebug && console.log(error);
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
  };

  const handleLimitChange = (value) => {
    setLimitFilter(value);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  const AddFilter = () => {
    setCurrentPage(1); // Reset to first page when applying filters
    setFilterTick((t) => t + 1);
    setFilterVisiblity(false);
  };

  const setStatusQuick = (status) => {
    setSearch((prev) => ({ ...prev, status }));
    setCurrentPage(1);
    setFilterTick((t) => t + 1);
  };

  async function fetchTeamData() {
    try {
      const res = await AxiosGet(ApiPaths.getTeams);
      if (res) dispatch(setTeamSection(res));
    } catch (error) {
      BasicInfo.isDebug && console.log(error);
    }
  }

  async function FetchData() {
    setLoading(true);
    try {
      const filteredSearch = Object.fromEntries(
        Object.entries(search).filter(([key, value]) => value !== "")
      );
      let queryParams;

      if (activeTab === "team") {
        queryParams = new URLSearchParams({
          levels: 1,
          page: currentPage,
          limit: limitFilter,
          from_level: 1,
          to_level: 1,
          ...filteredSearch,
        });
      } else if (activeTab === "level" && showLevelDetails && activeLevel) {
        queryParams = new URLSearchParams({
          levels: activeLevel,
          page: currentPage,
          limit: limitFilter,
          from_level: activeLevel,
          to_level: activeLevel,
          ...filteredSearch,
        });
      } else {
        // Default case - should not happen but for safety
        queryParams = new URLSearchParams({
          levels: 1,
          page: currentPage,
          limit: limitFilter,
          from_level: 2,
          to_level: 1,
          ...filteredSearch,
        });
      }

      const queryString = queryParams.toString();
      const res = await AxiosGet(`${ApiPaths.getALlTeam}?${queryString}`);
      BasicInfo.isDebug && console.log("response of direct team ", res?.data);
      setSelfBusiness(res?.totalSum?.business || 0);
      setTeamBusiness(res?.totalSum?.levelbusiness || 0);
      setLevelTeam(res?.data || []);
      setTotalRecords(res?.totalRecords || 0);
      setTotalPages(res?.totalPages || 1);
    } catch (e) {
      BasicInfo.isDebug && console.log("error", e);
    } finally {
      setLoading(false);
    }
  }

  async function ResetFun() {
    filterReset();
    setCurrentPage(1);
    setFilterVisiblity(false);
    setLoading(true);
    try {
      let queryParams;

      if (activeTab === "team") {
        queryParams = new URLSearchParams({
          levels: 1,
          page: 1,
          limit: 10,
          from_level: 1,
          to_level: 1,
        });
      } else if (activeTab === "level" && showLevelDetails && activeLevel) {
        queryParams = new URLSearchParams({
          levels: activeLevel,
          page: 1,
          limit: 10,
          from_level: activeLevel,
          to_level: activeLevel,
        });
      } else {
        queryParams = new URLSearchParams({
          levels: 1,
          page: 1,
          limit: 10,
          from_level: 1,
          to_level: 1,
        });
      }

      const queryString = queryParams.toString();

      const res = await AxiosGet(`${ApiPaths.getALlTeam}?${queryString}`);
      setLevelTeam(res?.data || []);
      setSelfBusiness(res?.totalSum?.business || 0);
      setTeamBusiness(res?.totalSum?.levelbusiness || 0);
      setTotalRecords(res?.totalRecords || 0);
      setTotalPages(res?.totalPages || 1);
    } catch (e) {
      BasicInfo.isDebug && console.log("error", e);
    } finally {
      setLoading(false);
    }
  }

  function filterReset() {
    setSearch(EMPTY_SEARCH);
    setLimitFilter(10);
    setCurrentPage(1);
  }

  const handleViewDetails = (member) => {
    setShowDetails(true);
    setSelectedMember(member);
  };

  const handleLevelDetails = (level) => {
    setShowLevelDetails(true);
    setSelectedLevelData(level);
    setActiveLevel(level.level);
    setCurrentPage(1); // Reset to first page when viewing level details
    setFilterVisiblity(false); // Hide filters when entering level details
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToLevels = () => {
    setShowLevelDetails(false);
    setSelectedLevelData(null);
    setActiveLevel(null);
    setCurrentPage(1);
    setLevelTeam([]);
    setTotalRecords(0);
    setTotalPages(0);
    setFilterVisiblity(false);
    filterReset();
  };

  useEffect(() => {
    if (activeTab === "openLevel") {
      fetchOpenLevels();
    }
  }, [activeTab]);

  const fetchOpenLevels = async () => {
    try {
      setLoading(true);
      const res = await AxiosGet(ApiPaths.previewOpenedLevels); // ensure key is correct
      if (res) {
        setOpenLevelData(res.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch open levels", err);
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setShowLevelDetails(false);
    setSelectedLevelData(null);
    setActiveLevel(null);
    setFilterVisiblity(false);
    filterReset();
  };

  // Escape closes sheets; the page behind doesn't scroll while one is open
  const sheetOpen = showDetails || filterVisiblity;
  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setShowDetails(false);
        setFilterVisiblity(false);
      }
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [sheetOpen]);

  /* ---------- derived ---------- */
  const totalTeam = Number(teamData?.sum?.total_team) || 0;
  const activeTeam = Number(teamData?.sum?.active_team) || 0;
  const inactiveTeam = Math.max(0, totalTeam - activeTeam);
  const activePct = totalTeam > 0 ? (activeTeam / totalTeam) * 100 : 0;
  const levels = Array.isArray(teamData?.teamSection) ? teamData.teamSection : [];
  const directCount = Number(levels[0]?.total_team) || 0;
  const maxLevelVolume = Math.max(1, ...levels.map((l) => Number(l.business) || 0));
  const activeFilterCount = ["name", "username", "startDateJoining", "endDateJoining"].filter(
    (k) => search[k] !== ""
  ).length + (Number(limitFilter) !== 10 ? 1 : 0);
  const listing = activeTab === "team" || (activeTab === "level" && showLevelDetails);

  /* ---------- pieces ---------- */
  const renderStatusChips = () => (
    <div className="dtChips" role="group" aria-label="Filter by status">
      {[
        { label: "All", value: "" },
        { label: "Active", value: "1" },
        { label: "Inactive", value: "0" },
      ].map((c) => (
        <button
          key={c.label}
          type="button"
          className={`dtChip ${search.status === c.value ? "isOn" : ""}`}
          onClick={() => setStatusQuick(c.value)}
        >
          {c.label}
        </button>
      ))}
    </div>
  );

  const renderListToolbar = () => (
    <div className="dtListBar">
      <button type="button" className="dtSearchBtn" onClick={() => setFilterVisiblity(true)}>
        <Search size={17} />
        <span>
          {search.name || search.username
            ? [search.name, search.username].filter(Boolean).join(" · ")
            : "Search name or user ID"}
        </span>
      </button>
      <button
        type="button"
        className={`dtFilterBtn ${activeFilterCount ? "isOn" : ""}`}
        onClick={() => setFilterVisiblity(true)}
        aria-label="Filters"
      >
        <SlidersHorizontal size={18} />
        {activeFilterCount > 0 && <i>{activeFilterCount}</i>}
      </button>
    </div>
  );

  const renderMember = (m, showLevelInfo, key) => {
    const on = m?.status == 1;
    return (
      <button key={key} type="button" className="dtMember" onClick={() => handleViewDetails(m)}>
        <span className="dtMemberTop">
          <span className={`dtAvatar ${on ? "" : "off"}`}>
            {initials(m?.name, m?.username)}
            <i />
          </span>
          <span className="dtMemberId">
            <b>{m?.name || "—"}</b>
            <small>
              @{m?.username}
              {showLevelInfo && m?.sponsor_username ? ` · via ${m.sponsor_username}` : ""}
            </small>
          </span>
          <span className={`dtStatus ${on ? "on" : "off"}`}>{on ? "Active" : "Inactive"}</span>
        </span>
        <span className="dtMemberStats">
          <span>
            <small>Team Business</small>
            <b>
              {cur}
              {fmt(m?.business || 0)}
            </b>
          </span>
          <span>
            <small>Self Package</small>
            <b>
              {cur}
              {fmt(m?.self_investment || 0)}
            </b>
          </span>
          <span>
            <small>{showLevelInfo ? "Direct" : "Team"}</small>
            <b>{showLevelInfo ? m?.direct_team ?? 0 : m?.total_team || 0}</b>
          </span>
        </span>
        <span className="dtMemberFoot">
          <span>
            <Calendar size={12} /> Joined {m?.joining_date ? moment(m.joining_date).format("DD MMM YYYY") : "—"}
          </span>
          {showLevelInfo && m?.rank ? <span className="dtRank">{m.rank}</span> : null}
          <ChevronRight size={16} className="dtMemberGo" />
        </span>
      </button>
    );
  };

  const renderSkeletons = () => (
    <div className="dtList">
      {[0, 1, 2].map((i) => (
        <div key={i} className="dtMember dtSkel">
          <span className="dtSkelLine w60" />
          <span className="dtSkelLine w90" />
          <span className="dtSkelLine w40" />
        </div>
      ))}
    </div>
  );

  const renderMemberList = (showLevelInfo) => (
    <>
      <div className="dtListHead">
        <h2>
          {showLevelInfo ? `Level ${selectedLevelData?.level} members` : "Direct members"}
          <span>{fmt(totalRecords)}</span>
        </h2>
        {totalRecords > 0 && (
          <span className="dtListSum">
            Business {cur}
            {fmt(selfBusiness || 0)}
          </span>
        )}
      </div>
      {loading && !levelTeam.length ? (
        renderSkeletons()
      ) : totalRecords === 0 ? (
        <div className="dtEmpty">
          <span>
            <Inbox size={26} />
          </span>
          <b>{showLevelInfo ? "No members on this level" : "No team members found"}</b>
          <small>
            {search.status || activeFilterCount
              ? "Try clearing your filters."
              : "Share your referral link to start building your team."}
          </small>
          {(search.status || activeFilterCount > 0) && (
            <button type="button" onClick={ResetFun}>
              <RotateCcw size={14} /> Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className={`dtList ${loading ? "isBusy" : ""}`}>
          {levelTeam?.map((m, idx) => (
            renderMember(m, showLevelInfo, m._id || idx)
          ))}
        </div>
      )}
      {totalRecords > 0 && (
        <div className="dtPager">
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePagination}
          />
        </div>
      )}
    </>
  );

  return (
    <section className="dtPage">
      {/* ---------- Hero ---------- */}
      <div className="dtHero">
        <div className="dtHeroTop">
          <div>
            <span className="dtEyebrow">
              <Users size={13} /> My Team
            </span>
            <div className="dtHeroValue">{fmt(totalTeam)}</div>
            <div className="dtHeroLabel">Total team members</div>
          </div>
          <div className="dtClock">
            <span>
              <Calendar size={12} />
              {currentTime.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            </span>
            <span>
              <Clock size={12} />
              {currentTime.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        </div>

        <div className="dtSplit" aria-label={`${activeTeam} active, ${inactiveTeam} inactive`}>
          <span className="dtSplitBar">
            <span style={{ width: `${activePct}%` }} />
          </span>
          <span className="dtSplitLegend">
            <span>
              <i className="on" /> {fmt(activeTeam)} active
            </span>
            <span>
              <i className="off" /> {fmt(inactiveTeam)} inactive
            </span>
            <b>{activePct.toFixed(0)}%</b>
          </span>
        </div>

        <div className="dtHeroStats">
          <div>
            <GitBranch size={16} />
            <b>{fmt(directCount)}</b>
            <small>Direct</small>
          </div>
          <div>
            <Layers size={16} />
            <b>{fmt(levels.length)}</b>
            <small>Levels</small>
          </div>
          <div>
            <Briefcase size={16} />
            <b>
              {cur}
              {fmt(teamData?.sum?.business || 0)}
            </b>
            <small>Team business</small>
          </div>
        </div>
      </div>

      {/* ---------- Tabs ---------- */}
      <div className="dtTabs" role="tablist">
        {[
          { key: "team", label: "Direct", Icon: UserCheck },
          { key: "level", label: "Levels", Icon: Layers },
          { key: "openLevel", label: "Open Levels", Icon: Unlock },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            className={`dtTab ${activeTab === key ? "isOn" : ""}`}
            onClick={() => switchTab(key)}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* ---------- Direct ---------- */}
      {activeTab === "team" && (
        <div className="dtPanel" key="team">
          {renderListToolbar()}
          {renderStatusChips()}
          {renderMemberList(false)}
        </div>
      )}

      {/* ---------- Levels ---------- */}
      {activeTab === "level" && (
        <div className="dtPanel" key={showLevelDetails ? `lv-${activeLevel}` : "levels"}>
          {showLevelDetails ? (
            <>
              <div className="dtLevelHead">
                <button type="button" className="dtBack" onClick={handleBackToLevels} aria-label="Back to levels">
                  <ArrowLeft size={19} />
                </button>
                <div>
                  <b>Level {selectedLevelData?.level}</b>
                  <small>
                    {fmt(selectedLevelData?.total_team || 0)} members · {cur}
                    {fmt(selectedLevelData?.business || 0)} volume
                  </small>
                </div>
              </div>
              {renderListToolbar()}
              {renderStatusChips()}
              {renderMemberList(true)}
            </>
          ) : (
            <>
              <div className="dtListHead">
                <h2>
                  Team by level <span>{levels.length}</span>
                </h2>
              </div>
              {levels.length ? (
                <div className="dtLevels">
                  {levels.map((level, idx) => {
                    const tone = levelTones[idx % levelTones.length];
                    const vol = Number(level.business) || 0;
                    const lvActive = Number(level.active_team);
                    return (
                      <button
                        key={level._id || idx}
                        type="button"
                        className={`dtLevel tone-${tone}`}
                        onClick={() => handleLevelDetails(level)}
                      >
                        <span className="dtLevelNo">
                          <small>LVL</small>
                          {level.level}
                        </span>
                        <span className="dtLevelBody">
                          <span className="dtLevelRow">
                            <b>
                              {fmt(level.total_team || 0)} member{Number(level.total_team) === 1 ? "" : "s"}
                            </b>
                            <span className="dtLevelVol">
                              {cur}
                              {fmt(vol)}
                            </span>
                          </span>
                          <span className="dtLevelBar">
                            <span style={{ width: `${(vol / maxLevelVolume) * 100}%` }} />
                          </span>
                          <span className="dtLevelMeta">
                            {isFinite(lvActive) ? `${fmt(lvActive)} active` : "Total volume"}
                            <span>View members</span>
                          </span>
                        </span>
                        <ChevronRight size={18} className="dtLevelGo" />
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="dtEmpty">
                  <span>
                    <Layers size={26} />
                  </span>
                  <b>No level teams yet</b>
                  <small>Levels appear as your direct members build their own teams.</small>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ---------- Open levels ---------- */}
      {activeTab === "openLevel" && (
        <div className="dtPanel" key="open">
          <div className="dtListHead">
            <h2>Open level preview</h2>
          </div>
          {loading && !openLevelData.length ? (
            renderSkeletons()
          ) : openLevelData.length === 0 ? (
            <div className="dtEmpty">
              <span>
                <Lock size={26} />
              </span>
              <b>No open level data found</b>
              <small>Level unlock requirements will show here.</small>
            </div>
          ) : (
            <div className="dtLevels">
              {openLevelData.map((item, idx) => {
                const bPct = Math.min(100, ((Number(item.business) || 0) / (Number(item.business_required) || 1)) * 100);
                const dPct = Math.min(
                  100,
                  ((Number(item.direct_business) || 0) / (Number(item.direct_business_required) || 1)) * 100
                );
                return (
                  <div key={idx} className={`dtOpen ${item.isOpen ? "isOpen" : ""}`}>
                    <div className="dtOpenHead">
                      <span className="dtLevelNo">
                        <small>LVL</small>
                        {item.level}
                      </span>
                      <span className={`dtOpenState ${item.isOpen ? "on" : "off"}`}>
                        {item.isOpen ? <Unlock size={13} /> : <Lock size={13} />}
                        {item.isOpen ? "Open" : "Locked"}
                      </span>
                      <span className="dtOpenIncome">
                        <b>{item.income}%</b>
                        <small>income</small>
                      </span>
                    </div>
                    <div className="dtReq">
                      <span>
                        Business
                        <b>
                          {cur}
                          {fmt(item.business)} / {cur}
                          {fmt(item.business_required)}
                        </b>
                      </span>
                      <span className="dtReqBar">
                        <span style={{ width: `${bPct}%` }} />
                      </span>
                    </div>
                    <div className="dtReq">
                      <span>
                        Direct business
                        <b>
                          {cur}
                          {fmt(item.direct_business)} / {cur}
                          {fmt(item.direct_business_required)}
                        </b>
                      </span>
                      <span className="dtReqBar">
                        <span style={{ width: `${dPct}%` }} />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ---------- Filter sheet ---------- */}
      {filterVisiblity && listing && (
        <div className="dtSheetOverlay" onClick={() => setFilterVisiblity(false)}>
          <div className="dtSheet" role="dialog" aria-modal="true" aria-labelledby="dtFilterTitle" onClick={(e) => e.stopPropagation()}>
            <span className="dtHandle" aria-hidden="true" />
            <div className="dtSheetHead">
              <h3 id="dtFilterTitle">Search & filters</h3>
              <button type="button" className="dtClose" onClick={() => setFilterVisiblity(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <label className="dtField">
              <span>Name</span>
              <input type="text" name="name" placeholder="Search by name" value={search.name} onChange={handleChange} />
            </label>
            <label className="dtField">
              <span>User ID</span>
              <input
                type="text"
                name="username"
                placeholder="Search by user ID"
                value={search.username}
                onChange={handleChange}
                autoCapitalize="characters"
                autoCorrect="off"
              />
            </label>
            <div className="dtFieldRow">
              <label className="dtField">
                <span>Joined from</span>
                <input type="date" name="startDateJoining" value={search.startDateJoining} onChange={handleChange} />
              </label>
              <label className="dtField">
                <span>Joined to</span>
                <input type="date" name="endDateJoining" value={search.endDateJoining} onChange={handleChange} />
              </label>
            </div>
            <div className="dtField">
              <span>Status</span>
              <div className="dtSeg">
                {[
                  { label: "All", value: "" },
                  { label: "Active", value: "1" },
                  { label: "Inactive", value: "0" },
                ].map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    className={search.status === s.value ? "isOn" : ""}
                    onClick={() => setSearch((p) => ({ ...p, status: s.value }))}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="dtField">
              <span>Per page</span>
              <div className="dtSeg">
                {[10, 20, 50, 100, 200].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={Number(limitFilter) === n ? "isOn" : ""}
                    onClick={() => handleLimitChange(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div className="dtSheetActions">
              <button type="button" className="dtBtn ghost" onClick={ResetFun}>
                <RotateCcw size={16} /> Reset
              </button>
              <button type="button" className="dtBtn primary" onClick={AddFilter}>
                <Search size={16} /> Show results
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Member detail sheet ---------- */}
      {showDetails && (
        <div className="dtSheetOverlay" onClick={() => setShowDetails(false)}>
          <div className="dtSheet" role="dialog" aria-modal="true" aria-labelledby="dtMemberTitle" onClick={(e) => e.stopPropagation()}>
            <span className="dtHandle" aria-hidden="true" />
            <button type="button" className="dtClose abs" onClick={() => setShowDetails(false)} aria-label="Close">
              <X size={18} />
            </button>
            <div className="dtDetailHead">
              <span className={`dtAvatar lg ${selectedMember?.status == 1 ? "" : "off"}`}>
                {initials(selectedMember?.name, selectedMember?.username)}
              </span>
              <div>
                <h3 id="dtMemberTitle">{selectedMember?.name || "—"}</h3>
                <small>@{selectedMember?.username}</small>
                <span className={`dtStatus ${selectedMember?.status == 1 ? "on" : "off"}`}>
                  {selectedMember?.status == 1 ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            <div className="dtDetailStats">
              <div>
                <small>Self Package</small>
                <b>
                  {cur}
                  {fmt(selectedMember?.self_investment || 0)}
                </b>
              </div>
              <div>
                <small>Team Business</small>
                <b>
                  {cur}
                  {fmt(selectedMember?.business || 0)}
                </b>
              </div>
            </div>

            <dl className="dtDetailList">
              <div>
                <dt>Email</dt>
                <dd>{selectedMember?.email || "—"}</dd>
              </div>
              <div>
                <dt>Mobile No.</dt>
                <dd>{selectedMember?.mobile || "—"}</dd>
              </div>
              <div>
                <dt>Rank</dt>
                <dd>{selectedMember?.rank || "—"}</dd>
              </div>
              {selectedMember?.level !== undefined && (
                <div>
                  <dt>Level</dt>
                  <dd>{selectedMember.level}</dd>
                </div>
              )}
              {selectedMember?.sponsor_username && (
                <div>
                  <dt>Sponsor</dt>
                  <dd>{selectedMember.sponsor_username}</dd>
                </div>
              )}
              <div>
                <dt>Date Of Joining</dt>
                <dd>{selectedMember?.joining_date ? moment(selectedMember.joining_date).format("lll") : "—"}</dd>
              </div>
              <div>
                <dt>Date Of Activation</dt>
                <dd>
                  {selectedMember?.Activation_date ? moment(selectedMember.Activation_date).format("lll") : "Not activated"}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </section>
  );
};

export default DirectTeam;
